import React, { Component } from 'react';
import { Animated, TouchableWithoutFeedback, StyleSheet, I18nManager } from 'react-native';

import { ViewPropTypes } from 'deprecated-react-native-prop-types';
import { has as _has, isFunction as _isFunction, get as _get } from 'lodash';
import memoize from 'memoize-one';
import PropTypes from 'prop-types';

import FoundationTestKeys from '../../FoundationTestKeys';
import { LightThemeColors, withTheme } from '../Themes';

import styles from './Toggle.Styles';
import ToggleSizesEnum from './ToggleSizesEnum';

class Toggle extends Component {
  static getDerivedStateFromProps(props, state) {
    if (_has(props, 'selected') && props.selected !== state.selected) {
      return { selected: props.selected };
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.state = {
      selected: props.initialValue,
    };

    this.animation = new Animated.Value(props.initialValue ? 1 : 0);
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.selected !== prevState.selected) {
      this.startAnimation(this.state.selected);
    }
  }

  onPress = () => {
    const newSelected = !this.state.selected;
    const usingProps = _has(this.props, 'selected');

    if (_isFunction(this.props.onChange)) {
      this.props.onChange(usingProps ? this.state.selected : newSelected);
    }

    if (!usingProps) {
      this.setState({ selected: newSelected });
    }
  };

  getProperStyles = (
    selected,
    size,
    withShadow,
    inactiveContainerStyle,
    activeContainerStyle,
    inactiveCircleStyle,
    activeCircleStyle,
    theme,
  ) => {
    const containerStyles = [
      styles.inactiveContainerStyle(theme),
      inactiveContainerStyle,
      styles[`${size}ContainerStyle`],
    ];
    const circleStyles = [
      styles.inactiveCircleStyle(theme),
      inactiveCircleStyle,
      styles[`${size}CircleStyle`],
    ];

    if (withShadow) {
      circleStyles.push(styles.shadowStyle(theme));
    }

    if (selected) {
      containerStyles.push(styles.activeContainerStyle(theme));
      containerStyles.push(activeContainerStyle);
      circleStyles.push(styles.activeCircleStyle(theme));
      circleStyles.push(activeCircleStyle);
    }

    return {
      containerStyle: StyleSheet.flatten(containerStyles),
      circleStyle: StyleSheet.flatten(circleStyles),
    };
  };

  getProperValuesForAnimation = (
    size,
    withShadow,
    inactiveContainerStyle,
    activeContainerStyle,
    inactiveCircleStyle,
    activeCircleStyle,
    theme,
  ) => {
    const inactiveContainerFinalStyles = StyleSheet.flatten([
      styles.inactiveContainerStyle(theme),
      inactiveContainerStyle,
      styles[`${size}ContainerStyle`],
    ]);
    const activeContainerFinalStyles = StyleSheet.flatten([
      inactiveContainerFinalStyles,
      styles.activeContainerStyle(theme),
      activeContainerStyle,
    ]);
    const inactiveCircleFinalStyles = StyleSheet.flatten([
      styles.inactiveCircleStyle(theme),
      inactiveCircleStyle,
      styles[`${size}CircleStyle`],
      withShadow ? styles.shadowStyle(theme) : undefined,
    ]);
    const activeCircleFinalStyles = StyleSheet.flatten([
      inactiveCircleFinalStyles,
      styles.activeCircleStyle,
      activeCircleStyle,
    ]);

    return {
      inactiveContainerFinalStyles,
      activeContainerFinalStyles,
      inactiveCircleFinalStyles,
      activeCircleFinalStyles,
    };
  };

  getProperStylesMemoized = memoize(this.getProperStyles);
  getProperValuesForAnimationMemoized = memoize(this.getProperValuesForAnimation);

  startAnimation = newSelectedValue => {
    Animated.spring(this.animation, {
      toValue: newSelectedValue ? 1 : 0,
      useNativeDriver: false,
    }).start();
  };

  render() {
    const {
      size,
      withShadow,
      inactiveContainerStyle,
      activeContainerStyle,
      inactiveCircleStyle,
      activeCircleStyle,
      theme,
      testID,
      ...props
    } = this.props;

    const properStyles = this.getProperStylesMemoized(
      this.state.selected,
      size,
      withShadow,
      inactiveContainerStyle,
      activeContainerStyle,
      inactiveCircleStyle,
      activeCircleStyle,
      theme,
    );
    const properValuesForAnimation = this.getProperValuesForAnimationMemoized(
      size,
      withShadow,
      inactiveContainerStyle,
      activeContainerStyle,
      inactiveCircleStyle,
      activeCircleStyle,
      theme,
    );

    const translateXValue =
      properValuesForAnimation.activeContainerFinalStyles.width -
      2 * properValuesForAnimation.activeContainerFinalStyles.paddingHorizontal -
      (properValuesForAnimation.activeCircleFinalStyles.width +
        (properValuesForAnimation.activeCircleFinalStyles.marginHorizontal || 0) * 2);

    const containerBackgroundColor = this.animation.interpolate({
      inputRange: [0, 1],
      outputRange: [
        properValuesForAnimation.inactiveContainerFinalStyles.backgroundColor,
        properValuesForAnimation.activeContainerFinalStyles.backgroundColor,
      ],
    });

    const circleBackgroundColor = this.animation.interpolate({
      inputRange: [0, 1],
      outputRange: [
        properValuesForAnimation.inactiveCircleFinalStyles.backgroundColor,
        properValuesForAnimation.activeCircleFinalStyles.backgroundColor,
      ],
    });

    const circleTranslateX = this.animation.interpolate({
      inputRange: [0, 1],
      outputRange: [0, I18nManager.isRTL ? -translateXValue : translateXValue],
    });

    const circleShadowHeightOffset = this.animation.interpolate({
      inputRange: [0, 1],
      outputRange: [
        _get(properValuesForAnimation.inactiveCircleFinalStyles, 'shadowOffset.height', 0),
        _get(properValuesForAnimation.activeCircleFinalStyles, 'shadowOffset.height', 0),
      ],
    });

    const circleShadowWidthOffset = this.animation.interpolate({
      inputRange: [0, 1],
      outputRange: [
        _get(properValuesForAnimation.inactiveCircleFinalStyles, 'shadowOffset.width', 0),
        _get(properValuesForAnimation.activeCircleFinalStyles, 'shadowOffset.width', 0),
      ],
    });

    const animatedContainerStyle = {
      backgroundColor: containerBackgroundColor,
    };

    const animatedCircleStyle = {
      backgroundColor: circleBackgroundColor,
      transform: [{ translateX: circleTranslateX }],
      shadowOffset: {
        width: circleShadowWidthOffset,
        height: circleShadowHeightOffset,
      },
    };

    return (
      <TouchableWithoutFeedback
        onPress={this.onPress}
        accessibilityState={{
          checked: this.state.selected,
          selected: this.state.selected,
        }}
        testID={testID ?? FoundationTestKeys.toggleTWF}
        {...props}
      >
        <Animated.View
          style={[properStyles.containerStyle, animatedContainerStyle]}
          testID={FoundationTestKeys.toggleAnimatedViewContainer}
        >
          <Animated.View
            style={[properStyles.circleStyle, animatedCircleStyle]}
            testID={FoundationTestKeys.toggleAnimatedViewCircle}
          />
        </Animated.View>
      </TouchableWithoutFeedback>
    );
  }
}

Toggle.defaultProps = {
  initialValue: false,
  size: ToggleSizesEnum.AVERAGE,
  withShadow: true,
  onChange: () => {},
  inactiveContainerStyle: {},
  inactiveCircleStyle: {},
  activeContainerStyle: {},
  activeCircleStyle: {},
  theme: LightThemeColors,
};

Toggle.propTypes = {
  initialValue: PropTypes.bool,
  size: PropTypes.oneOf(Object.values(ToggleSizesEnum)),
  withShadow: PropTypes.bool,
  selected: PropTypes.bool,
  onChange: PropTypes.func,
  inactiveContainerStyle: ViewPropTypes.style,
  inactiveCircleStyle: ViewPropTypes.style,
  activeContainerStyle: ViewPropTypes.style,
  activeCircleStyle: ViewPropTypes.style,
  theme: PropTypes.shape({
    name: PropTypes.string,
    isDark: PropTypes.bool,
    colors: PropTypes.object,
  }),
  testID: PropTypes.string,
};

export default withTheme(Toggle);
